/**
 * DayEntriesSheet — Bottom-Sheet-Modal für Detail zu einem Tag im Stundenkonto.
 *
 * Ronny 2026-07-28: Klick auf einen Woche-Tag öffnet Detail-Ansicht mit allen
 * Zeit-Einträgen des Tages. Admins können werte inline bearbeiten
 * (Arbeitszeit, Km Hin/Zurück, Verpflegung, Übernachtung).
 */
import { useState, useEffect } from "react";
import { authFetch, useAuth } from "../../App";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { X, Pencil, Save, Loader2, Briefcase, Car, Utensils, BedDouble, Clock, Coffee } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = "/api";

const hm = (m) => {
  const v = Math.abs(m || 0);
  return `${Math.floor(v / 60)}:${(v % 60).toString().padStart(2, "0")}`;
};

function parseHM(str) {
  const s = String(str || "").trim();
  if (!s) return 0;
  if (s.includes(":")) {
    const [h, m] = s.split(":");
    return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
  }
  const num = parseFloat(s.replace(",", "."));
  if (!isFinite(num)) return 0;
  return Math.round(num * 60);
}

export default function DayEntriesSheet({ isoDate, userId, userName, onClose }) {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const canAdminEdit = !!user?.permissions?.can_approve_entries;
  const isSelf = !userId || userId === user?.id;

  const reload = async () => {
    if (!isoDate) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("date_from", isoDate);
      params.set("date_to", isoDate);
      if (userId) params.set("user_id", userId);
      const r = await authFetch(`${API}/time-entries?${params.toString()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const list = await r.json();
      setEntries(list || []);
    } catch (e) {
      setError(e.message || "Laden fehlgeschlagen");
    }
    setLoading(false);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isoDate, userId]);

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({
      work_start: entry.work_start || "",
      work_end: entry.work_end || "",
      work_break: entry.break_minutes ?? entry.work_break ?? 0,
      work_hm: hm(entry.work_time_minutes || 0),
      km_outbound: entry.travel_outbound?.kilometers || 0,
      km_return: entry.travel_return?.kilometers || 0,
      meal_allowance: entry.meal_allowance || 0,
      overnight_stay: !!(entry.overnight_stay || entry.overnight),
    });
  };

  const saveEdit = async (entry) => {
    setSaving(true);
    try {
      const changes = [];

      // 1) Zeiten / Pause zuerst — Backend berechnet work_time_minutes neu.
      const oStart = entry.work_start || "";
      if (String(editForm.work_start || "") !== oStart) {
        changes.push({ field: "work_start", value: String(editForm.work_start || "") });
      }
      const oEnd = entry.work_end || "";
      if (String(editForm.work_end || "") !== oEnd) {
        changes.push({ field: "work_end", value: String(editForm.work_end || "") });
      }
      const oBreak = Number(entry.break_minutes ?? entry.work_break ?? 0);
      if (Number(editForm.work_break || 0) !== oBreak) {
        changes.push({ field: "work_break", value: Number(editForm.work_break || 0) });
      }

      // 2) Manuelle Arbeitszeit-Übersteuerung (nur falls Zeiten unverändert blieben)
      const originalMinutes = entry.work_time_minutes || 0;
      const newMinutes = parseHM(editForm.work_hm);
      const timeFieldsChanged = changes.some(c =>
        c.field === "work_start" || c.field === "work_end" || c.field === "work_break"
      );
      if (!timeFieldsChanged && newMinutes !== originalMinutes) {
        changes.push({ field: "work_minutes", value: newMinutes });
      }

      const oKm = entry.travel_outbound?.kilometers || 0;
      if (Number(editForm.km_outbound) !== oKm) {
        changes.push({ field: "km_outbound", value: Number(editForm.km_outbound) || 0 });
      }
      const rKm = entry.travel_return?.kilometers || 0;
      if (Number(editForm.km_return) !== rKm) {
        changes.push({ field: "km_return", value: Number(editForm.km_return) || 0 });
      }
      const oMeal = entry.meal_allowance || 0;
      if (Number(editForm.meal_allowance) !== oMeal) {
        changes.push({ field: "meal_allowance", value: Number(editForm.meal_allowance) || 0 });
      }
      const oOvn = !!(entry.overnight_stay || entry.overnight);
      if (!!editForm.overnight_stay !== oOvn) {
        changes.push({ field: "overnight_stay", value: !!editForm.overnight_stay });
      }

      for (const ch of changes) {
        const r = await authFetch(`${API}/time-entries/${entry.id}/admin-edit`, {
          method: "PATCH",
          body: JSON.stringify(ch),
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || `HTTP ${r.status}`);
        }
      }
      setEditingId(null);
      await reload();
    } catch (e) {
      setError(e.message || "Speichern fehlgeschlagen");
    }
    setSaving(false);
  };

  const totalMin = entries.reduce((s, e) => s + (e.work_time_minutes || 0), 0);
  const totalKm = entries.reduce(
    (s, e) => s + (e.travel_outbound?.kilometers || 0) + (e.travel_return?.kilometers || 0),
    0,
  );

  const dateLabel = (() => {
    try { return format(parseISO(isoDate), "EEEE, dd. MMMM yyyy", { locale: de }); }
    catch { return isoDate; }
  })();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60"
        onClick={onClose}
        data-testid="day-entries-sheet-overlay"
      >
        <motion.div
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="w-full sm:max-w-2xl bg-[#141721] rounded-t-2xl sm:rounded-2xl max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="day-entries-sheet"
        >
          {/* Header */}
          <div className="sticky top-0 bg-[#141721] border-b border-white/8 px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                Tagesdetail{userName ? ` · ${userName}` : ""}
              </div>
              <div className="text-lg font-bold text-white">{dateLabel}</div>
            </div>
            <button onClick={onClose}
              data-testid="day-entries-sheet-close"
              className="w-9 h-9 rounded-full hover:bg-white/[0.06] flex items-center justify-center text-gray-400">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Totals */}
          <div className="px-5 py-3 grid grid-cols-2 gap-2 border-b border-white/8">
            <div className="rounded-lg bg-white/[0.04] px-3 py-2">
              <div className="text-[10px] font-bold uppercase text-gray-500">Arbeitszeit</div>
              <div className="text-lg font-mono font-bold text-white">{hm(totalMin)}</div>
            </div>
            <div className="rounded-lg bg-white/[0.04] px-3 py-2">
              <div className="text-[10px] font-bold uppercase text-gray-500">Kilometer</div>
              <div className="text-lg font-mono font-bold text-white">{totalKm} km</div>
            </div>
          </div>

          {/* Entries */}
          <div className="p-5 space-y-3">
            {loading && (
              <div className="flex items-center justify-center py-8 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lädt…
              </div>
            )}
            {!loading && error && (
              <div className="text-sm text-red-400 py-4">Fehler: {error}</div>
            )}
            {!loading && !error && entries.length === 0 && (
              <div className="text-center py-8 text-gray-500 text-sm" data-testid="day-entries-empty">
                Keine Einträge an diesem Tag
              </div>
            )}
            {!loading && entries.map((e) => {
              const isEd = editingId === e.id;
              const canEdit = canAdminEdit || (isSelf && !e.is_billed);
              return (
                <div key={e.id} data-testid={`day-entry-${e.id}`}
                     className="rounded-lg border border-white/8 bg-white/[0.03] p-3">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-white flex-1 min-w-0">
                      <Briefcase className="w-4 h-4 text-blue-400 shrink-0" />
                      <span className="truncate">{e.project_name || "Projekt"}</span>
                    </div>
                    {!isEd && canEdit && (
                      <button
                        onClick={() => startEdit(e)}
                        className="text-[11px] px-2 py-1 rounded-md bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 flex items-center gap-1"
                        data-testid={`day-entry-edit-${e.id}`}
                      >
                        <Pencil className="w-3 h-3" /> Bearbeiten
                      </button>
                    )}
                    {isEd && (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-[11px] px-2 py-1 rounded-md bg-white/[0.06] text-gray-300 hover:bg-white/[0.10]"
                          disabled={saving}
                        >Abbrechen</button>
                        <button
                          onClick={() => saveEdit(e)}
                          className="text-[11px] px-2 py-1 rounded-md bg-emerald-500/25 text-emerald-200 hover:bg-emerald-500/35 flex items-center gap-1 disabled:opacity-50"
                          disabled={saving}
                          data-testid={`day-entry-save-${e.id}`}
                        >
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Speichern
                        </button>
                      </div>
                    )}
                  </div>

                  {!isEd && (
                    <div className="grid grid-cols-2 gap-2 text-[12px] text-gray-300">
                      <div className="col-span-2 flex items-center gap-2 flex-wrap pb-1 mb-1 border-b border-white/5" data-testid={`day-entry-times-${e.id}`}>
                        <Clock className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        <span className="text-gray-500">Von</span>
                        <span className="font-mono text-white" data-testid={`day-entry-start-${e.id}`}>{e.work_start || "—"}</span>
                        <span className="text-gray-600">·</span>
                        <span className="text-gray-500">Bis</span>
                        <span className="font-mono text-white" data-testid={`day-entry-end-${e.id}`}>{e.work_end || "—"}</span>
                        <span className="text-gray-600">·</span>
                        <Coffee className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        <span className="text-gray-500">Pause</span>
                        <span className="font-mono text-white" data-testid={`day-entry-break-${e.id}`}>
                          {Number(e.break_minutes ?? e.work_break ?? 0)}m
                        </span>
                      </div>
                      <div><span className="text-gray-500">Arbeit:</span> <span className="font-mono text-white">{hm(e.work_time_minutes || 0)}</span></div>
                      <div><span className="text-gray-500">Km:</span> <span className="font-mono text-white">{(e.travel_outbound?.kilometers || 0) + (e.travel_return?.kilometers || 0)} km</span></div>
                      <div><span className="text-gray-500">Verpfl.:</span> <span className="font-mono text-white">{Number(e.meal_allowance || 0).toFixed(2)} €</span></div>
                      <div><span className="text-gray-500">Übernachtung:</span> <span className="text-white">{(e.overnight_stay || e.overnight) ? "Ja" : "Nein"}</span></div>
                      {e.notes && (
                        <div className="col-span-2 text-gray-400 text-[11px] mt-1">
                          <span className="text-gray-500">Notiz:</span> {e.notes}
                        </div>
                      )}
                    </div>
                  )}

                  {isEd && (
                    <div className="grid grid-cols-3 gap-2 text-[12px]">
                      <label className="flex flex-col gap-0.5">
                        <span className="text-gray-500 text-[10px] uppercase tracking-wider">Von</span>
                        <input
                          type="time"
                          value={editForm.work_start || ""}
                          onChange={(ev) => setEditForm({...editForm, work_start: ev.target.value})}
                          className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-white font-mono"
                          data-testid={`edit-work-start-${e.id}`}
                        />
                      </label>
                      <label className="flex flex-col gap-0.5">
                        <span className="text-gray-500 text-[10px] uppercase tracking-wider">Bis</span>
                        <input
                          type="time"
                          value={editForm.work_end || ""}
                          onChange={(ev) => setEditForm({...editForm, work_end: ev.target.value})}
                          className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-white font-mono"
                          data-testid={`edit-work-end-${e.id}`}
                        />
                      </label>
                      <label className="flex flex-col gap-0.5">
                        <span className="text-gray-500 text-[10px] uppercase tracking-wider">Pause (min)</span>
                        <input
                          type="number" min="0" step="5"
                          value={editForm.work_break ?? 0}
                          onChange={(ev) => setEditForm({...editForm, work_break: ev.target.value})}
                          className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-white font-mono"
                          data-testid={`edit-work-break-${e.id}`}
                        />
                      </label>
                      <label className="flex flex-col gap-0.5 col-span-3">
                        <span className="text-gray-500 text-[10px] uppercase tracking-wider">Arbeit (hh:mm) — nur manuell übersteuern</span>
                        <input
                          type="text" inputMode="numeric"
                          value={editForm.work_hm}
                          onChange={(ev) => setEditForm({...editForm, work_hm: ev.target.value})}
                          className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-white font-mono"
                          data-testid={`edit-work-hm-${e.id}`}
                          placeholder="wird aus Von/Bis/Pause berechnet"
                        />
                      </label>
                      <label className="flex flex-col gap-0.5">
                        <span className="text-gray-500 text-[10px] uppercase tracking-wider">Km Hin</span>
                        <input
                          type="number" min="0" step="1"
                          value={editForm.km_outbound}
                          onChange={(ev) => setEditForm({...editForm, km_outbound: ev.target.value})}
                          className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-white font-mono"
                        />
                      </label>
                      <label className="flex flex-col gap-0.5">
                        <span className="text-gray-500 text-[10px] uppercase tracking-wider">Km Zurück</span>
                        <input
                          type="number" min="0" step="1"
                          value={editForm.km_return}
                          onChange={(ev) => setEditForm({...editForm, km_return: ev.target.value})}
                          className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-white font-mono"
                        />
                      </label>
                      <label className="flex flex-col gap-0.5">
                        <span className="text-gray-500 text-[10px] uppercase tracking-wider">Verpfl. (€)</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={editForm.meal_allowance}
                          onChange={(ev) => setEditForm({...editForm, meal_allowance: ev.target.value})}
                          className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-white font-mono"
                        />
                      </label>
                      <label className="col-span-3 flex items-center gap-2 mt-1">
                        <input
                          type="checkbox"
                          checked={!!editForm.overnight_stay}
                          onChange={(ev) => setEditForm({...editForm, overnight_stay: ev.target.checked})}
                        />
                        <BedDouble className="w-4 h-4 text-purple-300" />
                        <span className="text-gray-300">Übernachtung</span>
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
